"use client";
import { useEffect, useMemo, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import MMVCarLoan from "../MMVCarLoan";
import styles from "./MMVTopFold.module.scss";
import { getDaysUntilDateString } from "@utils/formatDate";
import Button from "@components/atoms/Button";
import IntentModal from "@components/molecules/IntentModal";
import RequestCallBackModal from "@components/molecules/RequestCallBackModal";
import StickyCTA from "@components/molecules/StickyCTA";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { postRequestCallback } from "@services/requestCallbackService";
import { inApp } from "@utils/platformDetection.client";
import GlobalIntentModal from "@components/molecules/GlobalIntentModal";
import ModalManager from "@components/molecules/ModalManager";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { AwardTrustMarker } from "@interfaces/trust-markers.types";
import { LoginSdkConfig } from "@enums/LoginSdkConfig";
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { formatPrice } from "@utils/numberFormatter";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
import { getClientCookie } from "@lib/cookies.client";
import { createOMSQuote } from "@services/oms-service";
import {
  getFeatureValue as getGrowthBookFeatureValue,
  loadGrowthbookFeatures,
} from "@utils/growthBook";
import MMVPriceBreakupV2 from "../MMVPriceBreakupV2";
import MMVHeroSectionV2 from "../MMVHeroSectionV2";
import {
  CALLBACK_BUYING_PERIOD,
  captureLead,
} from "@helpers/leadCapture";
import {
  getInsurancePlanSelectedFromCover,
  getNormalizedInsuranceCover,
  INSURANCE_COVER,
  type InsuranceCover,
  type PricingInsuranceCoverOption,
} from "@constants/insuranceCover";
import { toKebabCase } from "@utils/stringFormatters";

type Color = {
  id: string;
  hexCodes: string[];
  name: string;
  brandColor: string;
  Inventory_details: any;
  Adsp_details: any;
};

function MMVTopFold({
  variantDetails,
  carFinanceData,
  carFinanceBankDetails,
  isLoggedIn,
  slug,
  cityName,
  buyingIntent,
  isSellable,
  pageType,
  carAvailability,
  cityId,
  userDetails,
  savingBalance,
  environmentCompensation,
  trustMarkerBadge,
  awardTrustMarkers,
  showAwards,
  priceBreakupData,
  color,
  userId,
  showStickyCta = true,
  isUpsellFlow = false,
  onHeroCtaVisibilityChange,
}: any & {
  trustMarkerBadge?: string | null;
  awardTrustMarkers?: AwardTrustMarker[];
  showAwards?: boolean;
  priceBreakupData?: any;
  color?: string;
  showStickyCta?: boolean;
  isUpsellFlow?: boolean;
  onHeroCtaVisibilityChange?: (isVisible: boolean) => void;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const ACKODRIVE_OMS_QUOTE = "ackodrive20_oms";
  const { showLoader, hideLoader } = useGlobalLoader();
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);

  const {
    brand_name: brandName,
    model_name: modelName,
    variant_name: variantName,
    availableColors: colors,
    basic_features: basicFeatures,
    price: variantPrice,
  } = variantDetails || {};

  const {
    variant_color_id: baseColorId,
    inventory_alert_dict: inventoryAlertData,
  } = variantPrice || {};

  const selectedColor = useMemo(
    () => colors?.find((c: Color) => c.id === baseColorId),
    [colors, baseColorId]
  );

  const colorKey = useMemo(() => {
    if (color) return color;
    if (selectedColor?.brand_color) {
      return selectedColor.brand_color.toLowerCase().replace(/\s+/g, "-");
    }
    return (
      priceBreakupData?.default_color ||
      (priceBreakupData?.response
        ? Object.keys(priceBreakupData.response)[0]
        : null)
    );
  }, [color, selectedColor, priceBreakupData]);

  const colorData = useMemo(
    () => colorKey && priceBreakupData?.response?.[colorKey],
    [colorKey, priceBreakupData]
  );

  const getFeatureValue = (key: string) =>
    basicFeatures?.find((feature: any) => feature.key === key)?.value;

  const seatingCapacity = getFeatureValue("seat");
  const transmission = getFeatureValue("transmission");
  const fuelType = getFeatureValue("fuel_type");

  const mmvName = `${brandName} ${modelName} ${variantName}`;
  const deliveryTypeParam = searchParams?.get("deliveryType");
  const insuranceCoverParam = searchParams?.get("insuranceCover");
  const modelYearParam = searchParams?.get("model_year");
  const availableYears = useMemo(
    () =>
      priceBreakupData?.manufacturing_years?.map((year: string | number) =>
        String(year)
      ) || [],
    [priceBreakupData?.manufacturing_years]
  );
  const defaultYear = priceBreakupData?.default_year
    ? String(priceBreakupData.default_year)
    : "";

  const transformPriceData = (priceData: any, isExpress: boolean) => {
    if (!priceData) return null;

    const priceBreakup: Record<string, number> = {
      ex_showroom: parseFloat(String(priceData.ex_showroom_price || "0")),
    };

    const hasRegistrationAmount = Object.keys(
      priceData.pricing_components || {}
    ).some((key) => key === "Registration Amount");

    Object.entries(priceData.pricing_components || {}).forEach(
      ([key, value]) => {
        if (hasRegistrationAmount && key === "Registration amount") {
          return;
        }
        priceBreakup[key] = parseFloat(String(value || "0"));
      }
    );

    const discountComponents = priceData.discount_components || {};

    return {
      deal_type: isExpress ? "Inventory" : "Adsp",
      market_price: parseFloat(
        String(priceData.market_price || priceData.onroad_price || "0")
      ),
      final_price: String(
        priceData.onroad_price || priceData.market_price || "0"
      ),
      adsp_price: String(
        priceData.acko_drive_price || priceData.onroad_price || "0"
      ),
      savings: parseFloat(String(priceData.acko_drive_savings || "0")),
      price_breakup: priceBreakup,
      savings_breakup: {
        ackodiscount: parseFloat(String(priceData.acko_drive_savings || "0")),
        consumerdiscount: parseFloat(
          String(
            discountComponents.consumerdiscount ||
              discountComponents["Consumer Discount"] ||
              "0"
          )
        ),
        ripdiscount: parseFloat(
          String(
            discountComponents.ripdiscount ||
              discountComponents["RIP Discount"] ||
              "0"
          )
        ),
        special_discount: parseFloat(
          String(
            discountComponents.special_discount ||
              discountComponents["Special Discount"] ||
              "0"
          )
        ),
      },
      waiting_period: priceData.additional_attributes?.delivery_days || priceData.additional_attributes?.waiting_period || "",
      variant_color_id: baseColorId,
    };
  };

  const getPriceDataForInsuranceCover = (
    priceData: any,
    isExpress: boolean,
    insuranceCover: InsuranceCover
  ) => {
    if (!priceData) return null;

    const selectedCoverPricing = (priceData.insurance_covers || []).find(
      (cover: PricingInsuranceCoverOption) => cover.cover_type === insuranceCover
    );

    if (!selectedCoverPricing) {
      return transformPriceData(priceData, isExpress);
    }

    return transformPriceData(
      {
        ...priceData,
        pricing_components:
          selectedCoverPricing.pricing_components || priceData.pricing_components,
        discount_components:
          selectedCoverPricing.discount_components || priceData.discount_components,
        market_price:
          selectedCoverPricing.market_price ??
          selectedCoverPricing.on_road_price ??
          priceData.market_price,
        onroad_price:
          selectedCoverPricing.on_road_price ??
          selectedCoverPricing.market_price ??
          priceData.onroad_price,
        acko_drive_price:
          selectedCoverPricing.acko_drive_price ?? priceData.acko_drive_price,
        acko_drive_savings:
          selectedCoverPricing.acko_drive_savings ??
          priceData.acko_drive_savings,
      },
      isExpress
    );
  };

  const getSelectedRawPriceData = () => {
    if (!colorData) return null;

    const mappedDeliveryType = deliveryTypeParam
      ? (() => {
          const lowerParam = deliveryTypeParam.toLowerCase();
          if (lowerParam === "express" || lowerParam === "inventory") {
            return "express";
          }
          if (lowerParam === "standard" || lowerParam === "adsp") {
            return "standard";
          }
          return null;
        })()
      : null;

    const isExpressDelivery =
      mappedDeliveryType === "express" ||
      (!mappedDeliveryType && isLoggedIn && colorData?.expressPrice);

    return isExpressDelivery && colorData?.expressPrice
      ? colorData.expressPrice
      : colorData?.adspPrice;
  };

  const selectedRawPriceData = getSelectedRawPriceData();
  const isInventoryAvailable = useMemo(
    () =>
      colorData?.expressPrice &&
      parseFloat(String(colorData.expressPrice.market_price || "0")) > 0,
    [colorData]
  );

  const isOnlyInventoryAvailable = useMemo(
    () =>
      colorData?.expressPrice &&
      parseFloat(String(colorData.expressPrice.market_price || "0")) > 0 &&
      !(
        colorData?.adspPrice &&
        parseFloat(String(colorData.adspPrice.market_price || "0")) > 0
      ),
    [colorData]
  );

  const deliveryOptions = {
    inventory: colorData?.expressPrice
      ? transformPriceData(colorData.expressPrice, true)
      : null,
    adsp: colorData?.adspPrice
      ? transformPriceData(colorData.adspPrice, false)
      : null,
  };

  const insuranceCovers = useMemo<PricingInsuranceCoverOption[]>(
    () => selectedRawPriceData?.insurance_covers || [],
    [selectedRawPriceData]
  );

  const initialSelectedInsuranceCover = useMemo(() => {
    if (isUpsellFlow) {
      return INSURANCE_COVER.STANDARD;
    }

    if (insuranceCoverParam) {
      return getNormalizedInsuranceCover(insuranceCoverParam);
    }

    const selectedCover = insuranceCovers.find((cover) => cover.selected)?.cover_type;
    return getNormalizedInsuranceCover(selectedCover);
  }, [insuranceCoverParam, insuranceCovers, isUpsellFlow]);

  const [selectedInsuranceCover, setSelectedInsuranceCover] =
    useState<InsuranceCover>(initialSelectedInsuranceCover);

  const selectedPriceData = useMemo(() => {
    if (!selectedRawPriceData) return null;

    const isExpress = selectedRawPriceData === colorData?.expressPrice;

    return getPriceDataForInsuranceCover(
      selectedRawPriceData,
      isExpress,
      selectedInsuranceCover
    );
  }, [
    colorData?.expressPrice,
    selectedInsuranceCover,
    selectedRawPriceData,
  ]);

  const selectedDeliveryData = selectedPriceData || null;

  const [selectedDelivery, setSelectedDelivery] =
    useState(selectedDeliveryData);
  const [selectedModelYear, setSelectedModelYear] = useState<string>(
    modelYearParam || defaultYear || availableYears?.[0] || ""
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIntentModalOpen, setIsIntentModalOpen] = useState(false);
  const [isRequestCallBackModalOpen, setIsRequestCallBackModalOpen] =
    useState(false);
  const [tempSelectedDelivery, setTempSelectedDelivery] =
    useState(selectedDelivery);

  const [isHeroCtaVisible, setIsHeroCtaVisible] = useState(true);
  const [hasPassedPriceBreakupEnd, setHasPassedPriceBreakupEnd] =
    useState(false);

  const handleHeroCtaVisibilityChange = (isVisible: boolean) => {
    setIsHeroCtaVisible(isVisible);
    onHeroCtaVisibilityChange?.(isVisible);
  };

  const [isMobileAppIntentModalOpen, setIsMobileAppIntentModalOpen] =
    useState(false);
  const [pendingInsuranceCover, setPendingInsuranceCover] = useState<
    typeof INSURANCE_COVER.STANDARD | typeof INSURANCE_COVER.EXTENDED
  >(INSURANCE_COVER.STANDARD);

  const isMobileApp = inApp();

  const carDetails = {
    name: mmvName,
    color: {
      name: selectedColor?.brand_color || "Unknown Colour",
      hex: selectedColor?.base_colors_hexcodes || ["#000000"],
      type: selectedColor?.color_type || "",
    },
  };

  const shouldShowMobileAppIntent = () => {
    if (typeof window === "undefined") return false;
    if (isUpsellFlow) return false;
    if (!isMobileApp) return false;
    if (!isLoggedIn) return false;
    const sessionIntentCaptured = sessionStorage.getItem("buying_intent");
    if (sessionIntentCaptured) return false;

    return true;
  };

  const leadCaptureHandler: (isCallback?: boolean) => Promise<boolean> = async (
    isCallback = false
  ) => {
    const phoneNumber = userDetails?.phone || searchParams?.get("phone") || "";
    const planSelected = getInsurancePlanSelectedFromCover(
      selectedInsuranceCover
    );

    return captureLead({
      pageType,
      carAvailability,
      brandName,
      modelName,
      variantName,
      cityName,
      vehicleColor: selectedColor?.brand_color || "",
      loginTypeLabel: isCallback ? "Request callback" : "Get quote",
      componentName: "MMVTopFold",
      trackingProps: {
        plan_selected: planSelected,
      },
      buyingPeriod:
        isCallback || isUpsellFlow ? CALLBACK_BUYING_PERIOD : undefined,
      baseLeadData: {
        phone: phoneNumber,
        plan_selected: planSelected,
      },
      dumpData: {
        plan_selected: planSelected,
      },
      flowTypeLabel: "New Car",
    });
  };

  const handleModelYearChange = (year: string) => {
    if (year === selectedModelYear) return;

    showLoader(`Loading ${year} model`);
    setSelectedModelYear(year);

    const currentParams = new URLSearchParams(searchParams?.toString() || "");
    currentParams.set("model_year", year);

    // Handle delivery type based on selected year
    if (availableYears && availableYears.length >= 2) {
      const minYear = Math.min(
        ...availableYears.map((y: string) => parseInt(y))
      );
      const maxYear = Math.max(
        ...availableYears.map((y: string) => parseInt(y))
      );
      const selectedYearNum = parseInt(year);

      // If switching to min year, always set to inventory (express)
      if (selectedYearNum === minYear) {
        currentParams.set("deliveryType", "inventory");
      }
      // Let the refreshed year-specific pricing decide the best delivery option.
      else if (selectedYearNum === maxYear) {
        currentParams.delete("deliveryType");
      }
    }

    const queryString = currentParams.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

    router.push(newUrl);
  };

  const handleDeliveryTypeChange = (
    deliveryType: "express" | "standard" | "inventory" | "adsp"
  ) => {
    // Map express/standard to inventory/adsp, or use directly if inventory/adsp

    // For URL param, use the original format (express/standard) for user-facing, or inventory/adsp if that's what was passed
    const urlParam = deliveryType;

    // Check if we're on min year and trying to select standard
    if (availableYears && availableYears.length >= 2) {
      const minYear = Math.min(
        ...availableYears.map((y: string) => parseInt(y))
      );
      const maxYear = Math.max(
        ...availableYears.map((y: string) => parseInt(y))
      );
      const currentYear = selectedModelYear
        ? parseInt(selectedModelYear)
        : null;

      // If on min year and selecting adsp (standard), switch to max year with adsp
      if (
        currentYear === minYear &&
        (urlParam === "adsp" || urlParam === "standard")
      ) {
        showLoader(`Loading standard delivery pricing`);
        setSelectedModelYear(String(maxYear));
        const currentParams = new URLSearchParams(
          searchParams?.toString() || ""
        );
        currentParams.set("model_year", String(maxYear));
        currentParams.set("deliveryType", "adsp");
        const queryString = currentParams.toString();
        const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
        router.push(newUrl);
        return;
      }

      // If on max year and selecting inventory (express), check if express delivery is available
      if (
        currentYear === maxYear &&
        (urlParam === "inventory" || urlParam === "express")
      ) {
        // Check if express price is available for selected color
        const isExpressDeliveryAvailable =
          colorData?.expressPrice &&
          parseFloat(String(colorData.expressPrice.market_price || "0")) > 0;

        if (!isExpressDeliveryAvailable) {
          // Express delivery not available for max year, switch to min year with express
          showLoader(`Loading ${minYear} model with express delivery`);
          setSelectedModelYear(String(minYear));
          const currentParams = new URLSearchParams(
            searchParams?.toString() || ""
          );
          currentParams.set("model_year", String(minYear));
          currentParams.set("deliveryType", "inventory");
          const queryString = currentParams.toString();
          const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
          router.push(newUrl);
          return;
        }

        // Express delivery is available, proceed with express
        showLoader(`Loading express delivery pricing`);
        const currentParams = new URLSearchParams(
          searchParams?.toString() || ""
        );
        currentParams.set("deliveryType", "inventory");
        const queryString = currentParams.toString();
        const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
        router.push(newUrl);
        return;
      }
    }

    const showLoaderDeliveryType = deliveryType === 'adsp' ? 'standard' : deliveryType === 'inventory' ? "express" : deliveryType ;

    showLoader(`Loading ${showLoaderDeliveryType} delivery pricing`);

    const currentParams = new URLSearchParams(searchParams?.toString() || "");
    currentParams.set("deliveryType", urlParam);

    const queryString = currentParams.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

    router.push(newUrl);
  };

  // Intent Modal handlers
  const handleRequestCallback = async (intent?: string) => {
    setIsIntentModalOpen(false);
    setIsRequestCallBackModalOpen(true);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_help",
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_color: selectedColor?.brand_color,
        vehicle_variant: variantName,
        customer_name: userDetails.name,
        car_availability: carAvailability,
        email: userDetails.email,
        phone: userDetails.phone,
      },
    });
  };

  const handleCreateOMSQuote = async (
    insuranceCover: typeof INSURANCE_COVER.STANDARD | typeof INSURANCE_COVER.EXTENDED = INSURANCE_COVER.STANDARD
  ) => {
    try {
      const colorSlug = toKebabCase(selectedColor?.brand_color);
      const payload = {
        name: brandName,
        delivery_type:
          selectedDelivery?.deal_type?.toLowerCase() === "adsp"
            ? "Standard"
            : "Express",
        city: cityName,
        insurance_cover_type: insuranceCover,
        items: [
          {
            sku_id: slug,
            sku_type: "CAR",
            color_name: colorSlug || "",
            manufacturing_year: selectedModelYear || ""
          },
        ],
        customer_name: userDetails?.name,
        email: userDetails?.email,
        phone_number: userDetails?.phone,
        journey: "new_car",
      };
      const authCookie = getClientCookie("ackodrive_tokens");
      if (!authCookie) {
        hideLoader();
        setIsQuoteLoading(false);
        return;
      }

      const token = JSON.parse(authCookie).access;
      const quoteResponse = await createOMSQuote(payload, token);
      if (quoteResponse?.data?.quote_ekey) {
        const quoteUrl = isUpsellFlow
          ? `/quote/v2/${quoteResponse?.data?.quote_ekey}?upsell=true`
          : `/quote/v2/${quoteResponse?.data?.quote_ekey}`;
        router.push(quoteUrl);
        return;
      } else {
        console.error("Error creating quote:", quoteResponse);
      }
      hideLoader();
      setIsQuoteLoading(false);
    } catch (error) {
      console.error("Error creating quote:", error);
      hideLoader();
      setIsQuoteLoading(false);
    }
  };

  const handleRouting = async (
    intent?: string,
    insuranceCover: typeof INSURANCE_COVER.STANDARD | typeof INSURANCE_COVER.EXTENDED = INSURANCE_COVER.STANDARD
  ) => {
    if (isQuoteLoading) return;

    setIsQuoteLoading(true);
    showLoader("Loading Quote...");

    try {
      const showNewQuoteFlow = true;
      if (showNewQuoteFlow) {
        await handleCreateOMSQuote(insuranceCover);
      } else {
        const currentParams = new URLSearchParams(
          searchParams?.toString() || ""
        );
        const currentDeliveryType = selectedDelivery?.deal_type;

        if (currentDeliveryType) {
          currentParams.set(
            "deal_type",
            (currentDeliveryType || "").toLowerCase()
          );
          currentParams.set("color_id", selectedColor?.id);
        }
        if (selectedModelYear && availableYears && availableYears.length > 1) {
          currentParams.set("model_year", selectedModelYear);
        }
        currentParams.set("insurance_cover", insuranceCover);
        if (intent) {
          currentParams.set("intent", intent);
        }
        const quoteUrl = `/quote/${slug}?${currentParams.toString()}`;
        router.push(quoteUrl);
      }
    } catch (error) {
      console.error("Error Loading GrowthBook:", error);
      hideLoader();
      setIsQuoteLoading(false);
    }
  };

  const handleGetQuote = async (
    intent?: string,
    insuranceCover: typeof INSURANCE_COVER.STANDARD | typeof INSURANCE_COVER.EXTENDED = INSURANCE_COVER.STANDARD
  ) => {
    setPendingInsuranceCover(insuranceCover);
    
    if (isUpsellFlow) {
      await leadCaptureHandler();
      handleRouting(intent, insuranceCover);
      return;
    }

    if (sessionStorage.getItem("buying_intent") === "false") {
      leadCaptureHandler();
      handleRouting(intent, insuranceCover);
      return;
    }

    setIsIntentModalOpen(true);
  };

  const handleGetQuoteFromIntent = async (intent?: string) => {
    leadCaptureHandler();

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: "Intent",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_get_detailed_quote",
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_color: selectedColor?.brand_color,
        vehicle_variant: variantName,
        customer_name: userDetails.name,
        email: userDetails.email,
        phone: userDetails.phone,
        intent: intent,
        delivery_type: selectedDelivery?.deal_type,
        car_availability: carAvailability,
      },
    });

    setIsIntentModalOpen(false);
    handleRouting(intent, pendingInsuranceCover);
  };

  const handleInsuranceCoverChange = (
    insuranceCover: typeof INSURANCE_COVER.STANDARD | typeof INSURANCE_COVER.EXTENDED
  ) => {
    if (insuranceCover === selectedInsuranceCover) {
      return;
    }

    setSelectedInsuranceCover(insuranceCover);
  };

  const selectedInsuranceCoverLabel =
    getInsurancePlanSelectedFromCover(selectedInsuranceCover);

  useEffect(() => {
    trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
      pageType: pageType,
      additionalProps: {
        view_type: "view_page",
        view_name: "view_product_details_page",
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_variant: variantName,
        city_name: cityName,
        car_availability: carAvailability,
      },
    });
  }, []);

  useEffect(() => {
    hideLoader();
  }, [pathname, searchParams, hideLoader]);

  // Sync selectedDelivery when priceBreakupData or URL params change
  useEffect(() => {
    if (selectedPriceData) {
      setSelectedDelivery(selectedPriceData);
    }
  }, [selectedPriceData]);

  useEffect(() => {
    if (isUpsellFlow) {
      setSelectedInsuranceCover(INSURANCE_COVER.STANDARD);
      return;
    }

    const isCurrentCoverAvailable = insuranceCovers.some(
      (cover) => cover.cover_type === selectedInsuranceCover
    );

    if (!isCurrentCoverAvailable) {
      setSelectedInsuranceCover(initialSelectedInsuranceCover);
    }
  }, [
    initialSelectedInsuranceCover,
    insuranceCovers,
    isUpsellFlow,
    selectedInsuranceCover,
  ]);

  useEffect(() => {
    const queryYear = searchParams?.get("model_year");
    if (queryYear && queryYear !== selectedModelYear) {
      setSelectedModelYear(queryYear);
    }
  }, [searchParams, selectedModelYear]);

  useEffect(() => {
    if (isModalOpen) {
      setTempSelectedDelivery(selectedDelivery);
    }
  }, [isModalOpen, selectedDelivery]);

  // Effect to validate and correct URL params based on year and express delivery availability
  useEffect(() => {
    if (availableYears && availableYears.length >= 2) {
      const minYear = Math.min(
        ...availableYears.map((y: string) => parseInt(y))
      );
      const maxYear = Math.max(
        ...availableYears.map((y: string) => parseInt(y))
      );
      const currentYearParam = searchParams?.get("model_year");
      const deliveryTypeParam = searchParams
        ?.get("deliveryType")
        ?.toLowerCase();
      const isStandardDeliveryType =
        deliveryTypeParam === "adsp" || deliveryTypeParam === "standard";
      const isExpressDeliveryType =
        deliveryTypeParam === "inventory" || deliveryTypeParam === "express";

      if (!currentYearParam) return;
      const currentYear = parseInt(currentYearParam);

      // If on min year and deliveryType is adsp (standard), redirect to inventory (express)
      if (currentYear === minYear && isStandardDeliveryType) {
        const currentParams = new URLSearchParams(
          searchParams?.toString() || ""
        );
        currentParams.set("deliveryType", "inventory");
        const queryString = currentParams.toString();
        const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
        router.push(newUrl);
        return;
      }

      // If on max year, check express delivery availability and set delivery type accordingly
      if (currentYear === maxYear) {
        // Check if express price is available for selected color
        const isExpressDeliveryAvailable =
          colorData?.expressPrice &&
          parseFloat(String(colorData.expressPrice.market_price || "0")) > 0;

        // If express is not available, keep the selected year and switch to standard.
        if (!isExpressDeliveryAvailable && isExpressDeliveryType) {
          const currentParams = new URLSearchParams(
            searchParams?.toString() || ""
          );
          currentParams.set("deliveryType", "adsp");
          const queryString = currentParams.toString();
          const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
          router.push(newUrl);
          return;
        }

        // If express is not available and delivery type is not set or is not adsp, set to adsp on max year
        if (
          !isExpressDeliveryAvailable &&
          deliveryTypeParam &&
          !isStandardDeliveryType
        ) {
          const currentParams = new URLSearchParams(
            searchParams?.toString() || ""
          );
          currentParams.set("deliveryType", "adsp");
          const queryString = currentParams.toString();
          const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
          router.push(newUrl);
          return;
        }
      }
    }
  }, [searchParams, availableYears, pathname, router, colorData]);

  useEffect(() => {
    if (shouldShowMobileAppIntent()) {
      setIsMobileAppIntentModalOpen(true);
    }
  }, []);

  return (
    <>
      <MMVHeroSectionV2
        mmvName={mmvName}
        brandName={brandName}
        modelName={modelName}
        variantName={variantName}
        colors={colors}
        selectedColor={selectedColor}
        seatingCapacity={seatingCapacity}
        fuelType={fuelType}
        transmission={transmission}
        isLoggedIn={isLoggedIn}
        isInventoryAvailable={isInventoryAvailable}
        deliveryOptions={deliveryOptions}
        selectedDelivery={selectedDelivery}
        setIsModalOpen={setIsModalOpen}
        setIsIntentModalOpen={setIsIntentModalOpen}
        handleGetQuote={() => handleGetQuote(undefined, selectedInsuranceCover)}
        cityName={cityName}
        inventoryAlertData={inventoryAlertData}
        onCtaVisibilityChange={handleHeroCtaVisibilityChange}
        isSellable={isSellable}
        carAvailability={carAvailability}
        pageType={pageType}
        trustMarkerBadge={trustMarkerBadge}
        awardTrustMarkers={awardTrustMarkers}
        showAwards={showAwards}
        selectedYear={selectedModelYear}
        onYearChange={handleModelYearChange}
        availableYears={availableYears}
        isOnlyInventoryAvailable={isOnlyInventoryAvailable}
        isUpsellFlow={isUpsellFlow}
        selectedInsuranceCoverLabel={selectedInsuranceCoverLabel}
      />
      <MMVPriceBreakupV2
        price={selectedDelivery}
        isLoggedIn={isLoggedIn}
        selectedDelivery={selectedDelivery}
        setIsModalOpen={setIsModalOpen}
        setIsIntentModalOpen={setIsIntentModalOpen}
        isInventoryAvailable={isInventoryAvailable}
        brandName={brandName}
        modelName={modelName}
        variantName={variantName}
        cityName={cityName}
        isSellable={isSellable}
        pageType={pageType}
        carAvailability={carAvailability}
        selectedColor={selectedColor}
        savingBalance={savingBalance}
        environmentCompensation={environmentCompensation}
        fuelType={fuelType}
        selectedYear={selectedModelYear}
        onYearChange={handleModelYearChange}
        availableYears={availableYears}
        insuranceCovers={insuranceCovers}
        selectedInsuranceCover={selectedInsuranceCover}
        onInsuranceCoverChange={handleInsuranceCoverChange}
        onGetQuoteWithInsurance={() =>
          handleGetQuote(undefined, selectedInsuranceCover)
        }
        isUpsellFlow={isUpsellFlow}
        onPriceBreakupEndReachedChange={setHasPassedPriceBreakupEnd}
      />
      {!isUpsellFlow && isSellable && variantDetails?.is_car_finance_active && (
        <MMVCarLoan
          price={selectedDelivery}
          carFinanceData={carFinanceData}
          carFinanceBankData={carFinanceBankDetails}
          isLoggedIn={isLoggedIn}
          brandName={brandName}
          modelName={modelName}
          variantName={variantName}
          cityName={cityName}
          isSellable={isSellable}
          pageType={pageType}
          carAvailability={carAvailability}
        />
      )}
      {showStickyCta && isSellable && hasPassedPriceBreakupEnd && (
        <StickyCTA
          stockCount={inventoryAlertData?.alert_units}
          onTalkToUs={async () => {
            const results = await Promise.allSettled([
              leadCaptureHandler(true), // Pass true to indicate this is a callback request with high intent
              postRequestCallback({
                callback_message: `Request callback for ${brandName} ${modelName} ${variantName} in ${cityName}`,
              }),
            ]);
            const [, callbackResult] = results;
            if (
              // @fix #001 - Invariant: callback success UI must depend on the callback request, not lead capture side effects.
              callbackResult.status === "fulfilled" &&
              callbackResult.value?.success
            ) {
              handleRequestCallback();
            }
          }}
          onGetQuote={async () => {
            if (isLoggedIn) {
              await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
                pageType: pageType,
                additionalProps: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_get_detailed_quote",
                  plan_selected: selectedInsuranceCoverLabel,
                  car_availability: carAvailability,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  vehicle_variant: variantName,
                  city_name: cityName,
                  section: "Sticky CTA",
                },
              });
              leadCaptureHandler();
              handleGetQuote(undefined, selectedInsuranceCover);
            } else {
              await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
                pageType: pageType,
                additionalProps: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_login_to_save",
                  plan_selected: selectedInsuranceCoverLabel,
                  car_availability: carAvailability,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  vehicle_variant: variantName,
                  city_name: cityName,
                  section: "Sticky CTA",
                },
              });
              const mmvDetails = {
                make: brandName || "",
                model: modelName || "",
                variant: variantName || "",
                color: selectedColor?.brand_color || "",
              };
              if (typeof window !== "undefined") {
                sessionStorage.setItem("loginType", "Reveal price");
                sessionStorage.setItem("flowType", "New Car");
                localStorage.setItem(
                  "MMV",
                  JSON.stringify({
                    make: mmvDetails?.make || "",
                    model: mmvDetails?.model || "",
                    variant: mmvDetails?.variant || "",
                    color: mmvDetails?.color || "",
                  })
                );
              }
              const loginUrl = getLoginUrl(
                addCaptureIntentIfNeeded(
                  window.location.href,
                  mmvDetails,
                  "Reveal price",
                  pageType
                )
              );
              try {
                showLoader("Redirecting to login...");
                await handleLoginFlow(loginUrl, LoginSdkConfig.AD_PRICE);
              } catch (error) {
                console.error("Error redirecting to quote login:", error);
                hideLoader();
              }
            }
          }}
          isLoggedIn={isLoggedIn}
        />
      )}
      {isModalOpen && (
        <ModalManager
          isOpen={isModalOpen}
          onClose={() => {
            setTempSelectedDelivery(selectedDelivery); // revert to previous on close
            setIsModalOpen(false);
          }}
          title="Select your delivery option"
          // className={styles.deliveryModal}
        >
          <div className={styles.deliveryOptions}>
            {/* Express Delivery Option */}
            <div
              className={
                `${styles.deliveryOptionCard} ` +
                (tempSelectedDelivery?.deal_type?.toLowerCase() === "inventory"
                  ? styles.selected
                  : "")
              }
              onClick={() => {
                setTempSelectedDelivery(deliveryOptions.inventory);
              }}
            >
              <div className={styles.deliveryOptionDetails}>
                <span
                  className={`${styles.deliveryOptionTitle} ${styles.expressTitle}`}
                >
                  <Image
                    src={`${assetLoader()}/icons/thunder.svg`}
                    alt="Express Delivery"
                    width={16}
                    height={16}
                    loading="lazy"
                  />
                  <span>Express Delivery</span>
                </span>
                <div className={styles.deliveryOptionSubRow}>
                  <span className={styles.deliveryOptionSubLabel}>
                    Get your car in
                  </span>
                  <span className={styles.deliveryOptionDays}>
                    {deliveryOptions.inventory?.waiting_period || 90}{" "}
                    days
                  </span>
                </div>
              </div>
              <span className={styles.deliveryOptionPrice}>
                {deliveryOptions.inventory?.adsp_price
                  ? formatPrice(
                      parseFloat(String(deliveryOptions.inventory.adsp_price))
                    )
                  : "--"}{" "}
              </span>
            </div>
            {/* Standard Delivery Option */}
            <div
              className={
                `${styles.deliveryOptionCard} ` +
                `${styles.standardCard} ` +
                (tempSelectedDelivery?.deal_type?.toLowerCase() === "adsp"
                  ? styles.selected
                  : "")
              }
              onClick={() => {
                setTempSelectedDelivery(deliveryOptions.adsp);
              }}
            >
              <div className={styles.deliveryOptionDetails}>
                <span className={styles.deliveryOptionTitle}>
                  Standard Delivery
                </span>
                <div className={styles.deliveryOptionSubRow}>
                  <span className={styles.deliveryOptionSubLabel}>
                    Get your car in
                  </span>
                  <span className={styles.deliveryOptionDays}>
                    {deliveryOptions.adsp?.waiting_period || 90}{" "}
                    days
                  </span>
                </div>
              </div>
              <span className={styles.deliveryOptionPrice}>
                {deliveryOptions.adsp?.adsp_price
                  ? formatPrice(
                      parseFloat(String(deliveryOptions.adsp.adsp_price))
                    )
                  : "--"}
              </span>
            </div>

            <Button
              className={styles.selectButton}
              onClick={() => {
                const selectedDealType =
                  tempSelectedDelivery?.deal_type?.toLowerCase();
                // Map to delivery type for URL
                const deliveryTypeToSet =
                  selectedDealType === "inventory" ||
                  tempSelectedDelivery === deliveryOptions.inventory
                    ? "inventory"
                    : "adsp";
                handleDeliveryTypeChange(
                  deliveryTypeToSet as "express" | "standard"
                );
                setSelectedDelivery(tempSelectedDelivery);
                setIsModalOpen(false);
              }}
            >
              {"Select"}
            </Button>
          </div>
        </ModalManager>
      )}
      {!isUpsellFlow && isIntentModalOpen && (
        <IntentModal
          isOpen={isIntentModalOpen}
          onClose={() => setIsIntentModalOpen(false)}
          carDetails={carDetails}
          onRequestCallback={handleRequestCallback}
          onGetQuote={handleGetQuoteFromIntent}
          buyingIntent={buyingIntent}
          cityName={cityName}
          mmvDetails={{
            make: brandName,
            model: modelName,
            variant: variantName,
            color: selectedColor?.brand_color || "",
          }}
          highPriorityCapture={false}
          pageType={pageType}
          loginType={"Get quote"}
        />
      )}
      {isRequestCallBackModalOpen && (
        <RequestCallBackModal
          isOpen={isRequestCallBackModalOpen}
          onClose={() => setIsRequestCallBackModalOpen(false)}
        />
      )}

      {/* Mobile App Intent Modal */}
      {!isUpsellFlow && isMobileAppIntentModalOpen && (
        <GlobalIntentModal
          isOpen={isMobileAppIntentModalOpen}
          onClose={() => {
            setIsMobileAppIntentModalOpen(false);
            const currentParams = searchParams ? searchParams.toString() : "";
            const newSearchParams = new URLSearchParams(currentParams);
            newSearchParams.delete("captureIntent");
            const newUrl = newSearchParams.toString()
              ? `${pathname}?${newSearchParams.toString()}`
              : pathname;
            router.replace(newUrl || "");
          }}
          buyingIntent={buyingIntent}
          cityName={cityName}
          mmvDetails={{
            make: brandName,
            model: modelName,
            variant: variantName,
            color: selectedColor?.brand_color || "",
          }}
          pageType={pageType}
          loginType={"Get quote"}
          disableClose
        />
      )}
    </>
  );
}

export default MMVTopFold;
